/**
 * Screen 3 — New subscription. The primary flow of the whole system.
 *
 * Target: a staff member with no training adds a subscriber in under 30
 * seconds. Everything here serves that.
 *
 * The price comes from the City View schedule and depends on four things:
 * age group (from the class), group or private, club-house member or not, and
 * the term. It is a LOOKUP, never a multiplication — 3 months costs less than
 * 3 × 1 month, so multiplying a monthly rate would overcharge every long sale.
 */
import { useEffect, useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/db';
import {
  createSubscription,
  findMembersByPhone,
  normalizeName,
  getCoachAssignments,
  getSelectableCoaches,
  validateNewSubscription,
  type NewSubscriptionResult,
} from '../db/operations';
import ClassPicker from '../components/ClassPicker';
import MoneyInput from '../components/MoneyInput';
import Receipt from './Receipt';
import { classLabel, findClass } from '@shared/catalog';
import { formatEgp } from '@shared/money';
import { computeEndDate, formatDisplayDate, isValidISODate, todayISO } from '@shared/dates';
import {
  computeTotalDue,
  computeRemaining,
  defaultPriceFor,
  effectiveMonthlyRate,
  priceKey,
  TERM_MONTHS,
  TRAINING_MODE_LABELS,
  type MembershipType,
  type TermMonths,
  type TrainingMode,
} from '@shared/pricing';
import { PAYMENT_METHOD_LABELS, PAYMENT_METHODS, type PaymentMethod } from '@shared/types';
import type { Member } from '@shared/types';

export interface Prefill {
  member: Member;
  classKey: string;
  months: TermMonths;
  private: boolean;
  /** Carried across a renewal so the same coach keeps the member by default. */
  privateCoach?: string;
  clubMember: boolean;
  startDate: string;
  isRenewal: boolean;
}

export default function NewSubscription({ prefill }: { prefill?: Prefill }) {
  /* ---- form state ---------------------------------------------------- */
  const [fullName, setFullName] = useState(prefill?.member.full_name ?? '');
  const [phone, setPhone] = useState(prefill?.member.phone ?? '');
  const [notes, setNotes] = useState('');
  const [clubMember, setClubMember] = useState(prefill?.clubMember ?? false);
  const [classKey, setClassKey] = useState(prefill?.classKey ?? '');
  const [isPrivate, setIsPrivate] = useState(prefill?.private ?? false);
  const [privateCoach, setPrivateCoach] = useState(prefill?.privateCoach ?? '');
  const [months, setMonths] = useState<TermMonths>(prefill?.months ?? 1);
  const [startDate, setStartDate] = useState(prefill?.startDate ?? todayISO());
  const [discount, setDiscount] = useState(0);
  const [paidNow, setPaidNow] = useState(0);
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [receivedBy, setReceivedBy] = useState('');

  const [showExtras, setShowExtras] = useState(Boolean(prefill));
  const [existingMember, setExistingMember] = useState<Member | null>(prefill?.member ?? null);
  const [duplicates, setDuplicates] = useState<Member[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [done, setDone] = useState<NewSubscriptionResult | null>(null);
  const [touched, setTouched] = useState(false);
  const [confirmedDifferent, setConfirmedDifferent] = useState(false);

  /* ---- price lookup --------------------------------------------------- */
  const schedule = useLiveQuery(() => db.price_schedule.toArray(), [], []);

  /* ---- who can take the privates -------------------------------------- */
  const coachOptions = useLiveQuery(() => getSelectableCoaches(), [], [] as string[]);
  const assignments = useLiveQuery(
    () => getCoachAssignments(),
    [],
    {} as Record<string, string>
  );
  const classCoach = classKey ? assignments[classKey] : undefined;

  const ageGroup = findClass(classKey)?.ageGroup === 'Kids' ? 'kids' : 'adults';
  const mode: TrainingMode = isPrivate ? 'private' : 'group';
  const membership: MembershipType = clubMember ? 'club_member' : 'non_member';

  const price = useMemo(() => {
    if (!classKey) return 0;
    const key = priceKey(ageGroup, mode, membership, months);
    const row = schedule.find((r) => r.price_key === key && r.deleted_at === null);
    return row?.price_piastres ?? defaultPriceFor(ageGroup, mode, membership, months);
  }, [schedule, classKey, ageGroup, mode, membership, months]);

  const totalDue = computeTotalDue(price, discount);
  const remaining = computeRemaining(totalDue, paidNow);
  const endDate = isValidISODate(startDate) ? computeEndDate(startDate, months) : '';

  /* ---- duplicate phone lookup ---------------------------------------- */
  useEffect(() => {
    let cancelled = false;
    findMembersByPhone(phone).then((found) => {
      if (!cancelled) setDuplicates(existingMember ? [] : found);
    });
    return () => {
      cancelled = true;
    };
  }, [phone, existingMember]);

  // Same name as well as same phone: almost certainly the same person again.
  const exactMatch = existingMember
    ? undefined
    : duplicates.find((m) => normalizeName(m.full_name) === normalizeName(fullName));
  const exactMatchId = exactMatch?.id;
  useEffect(() => {
    setConfirmedDifferent(false);
  }, [exactMatchId]);

  const errors = validateNewSubscription({
    ...(existingMember ? { existingMemberId: existingMember.id } : {}),
    fullName,
    phone,
    clubMember,
    classKey,
    private: isPrivate,
    privateCoach,
    months,
    startDate,
    pricePiastres: price,
    discountPiastres: discount,
    amountPaidNowPiastres: paidNow,
  });
  const canSave =
    Object.keys(errors).length === 0 && !saving && (!exactMatch || confirmedDifferent);

  async function save() {
    setTouched(true);
    if (!canSave) return;
    setSaving(true);
    setSaveError('');
    try {
      const result = await createSubscription({
        ...(existingMember ? { existingMemberId: existingMember.id } : {}),
        fullName,
        phone,
        notes,
        clubMember,
        classKey,
        private: isPrivate,
        privateCoach,
        months,
        startDate,
        pricePiastres: price,
        discountPiastres: discount,
        amountPaidNowPiastres: paidNow,
        paymentMethod: method,
        receivedBy: receivedBy.trim() || 'Front desk',
        ...(exactMatch && confirmedDifferent ? { confirmedDifferentPerson: true } : {}),
      });
      setDone(result);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Could not save');
    } finally {
      setSaving(false);
    }
  }

  function reset() {
    setFullName('');
    setPhone('');
    setNotes('');
    setClubMember(false);
    setClassKey('');
    setIsPrivate(false);
    setMonths(1);
    setStartDate(todayISO());
    setDiscount(0);
    setPaidNow(0);
    setMethod('cash');
    setExistingMember(null);
    setDuplicates([]);
    setConfirmedDifferent(false);
    setDone(null);
    setSaveError('');
    setTouched(false);
    setShowExtras(false);
  }

  if (done) return <Receipt result={done} onNew={reset} />;

  const err = (key: string) => (touched ? errors[key] : undefined);

  return (
    <div className="mx-auto max-w-7xl px-5 py-6">
      <h1 className="mb-6 text-4xl text-brand">
        {prefill?.isRenewal ? 'Renew subscription' : 'New subscription'}
      </h1>

      <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
        <div className="space-y-6">
          {/* Member */}
          <section className="card p-6">
            <h2 className="mb-4 text-xl text-paper">Member</h2>

            {existingMember ? (
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-btn border border-brand/40 bg-brand/10 p-4">
                <div>
                  <div className="tabular text-sm text-brand">{existingMember.sk_id}</div>
                  <div className="text-lg font-semibold" dir="auto">
                    {existingMember.full_name}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setExistingMember(null)}
                  className="rounded-btn border border-edge px-4 py-2 text-sm font-bold uppercase tracking-button hover:border-brand"
                >
                  Use a new member
                </button>
              </div>
            ) : (
              <div className="mb-4 grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="label" htmlFor="fullName">
                    Full name
                  </label>
                  <input
                    id="fullName"
                    autoFocus
                    dir="auto"
                    className="field"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Ahmed Hassan"
                  />
                  {err('fullName') && (
                    <p className="mt-1 text-sm text-status-expired">{err('fullName')}</p>
                  )}
                </div>
                <div>
                  <label className="label" htmlFor="phone">
                    Phone
                  </label>
                  <input
                    id="phone"
                    inputMode="tel"
                    className="field tabular"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="01012345678"
                  />
                </div>
              </div>
            )}

            {/* Club house membership — drives the whole price column. */}
            <div>
              <div className="label">Club house</div>
              <div className="flex gap-2">
                <Toggle
                  active={clubMember}
                  onClick={() => setClubMember(true)}
                  label="Club member"
                />
                <Toggle
                  active={!clubMember}
                  onClick={() => setClubMember(false)}
                  label="Non-member"
                />
              </div>
            </div>

            {duplicates.length > 0 && !existingMember && (
              <div className="mt-4 rounded-btn border border-status-expiring/40 bg-status-expiringBg p-4">
                <p className="mb-3 text-sm font-semibold text-status-expiring">
                  This number is already registered. Add the class to the existing member, or
                  carry on to create a separate one.
                </p>
                <div className="flex flex-wrap gap-2">
                  {duplicates.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => {
                        setExistingMember(m);
                        setFullName(m.full_name);
                        setClubMember(m.club_member);
                      }}
                      className="rounded-btn border border-status-expiring/50 px-4 py-2 text-sm font-semibold hover:bg-status-expiring/20"
                    >
                      <span className="tabular text-status-expiring">{m.sk_id}</span> ·{' '}
                      {m.full_name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {exactMatch && (
              <div className="mt-4 rounded-btn border border-status-expired/40 bg-status-expiredBg p-4">
                <p className="mb-3 text-sm font-semibold text-status-expired">
                  <span className="tabular">{exactMatch.sk_id}</span>{' '}
                  <span dir="auto">{exactMatch.full_name}</span> is already registered with this
                  name and phone. Use the existing member.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setExistingMember(exactMatch);
                    setFullName(exactMatch.full_name);
                    setClubMember(exactMatch.club_member);
                  }}
                  className="mb-3 rounded-btn border border-status-expired/50 px-4 py-2 text-sm font-semibold hover:bg-status-expired/20"
                >
                  Use this member
                </button>
                <label className="flex items-center gap-2 text-sm text-white/70">
                  <input
                    type="checkbox"
                    checked={confirmedDifferent}
                    onChange={(e) => setConfirmedDifferent(e.target.checked)}
                  />
                  This is a different person with the same name and phone
                </label>
              </div>
            )}
          </section>

          {/* Class */}
          <section className="card p-6">
            <h2 className="mb-4 text-xl text-paper">Class</h2>
            <ClassPicker value={classKey} onChange={setClassKey} />
            {err('classKey') && (
              <p className="mt-2 text-sm text-status-expired">{err('classKey')}</p>
            )}

            <div className="mt-5 border-t border-edge pt-4">
              <div className="label">Training</div>
              <div className="flex gap-2">
                <Toggle
                  active={!isPrivate}
                  onClick={() => setIsPrivate(false)}
                  label={TRAINING_MODE_LABELS.group}
                />
                <Toggle
                  active={isPrivate}
                  onClick={() => setIsPrivate(true)}
                  label={TRAINING_MODE_LABELS.private}
                />
              </div>
            </div>

            {/*
              A private hour is NOT automatically the class coach's. Whoever is
              picked here earns the 40% on this subscription, so it is asked
              rather than assumed — and the sale cannot be saved without it.
            */}
            {isPrivate && (
              <div className="mt-5 border-t border-edge pt-4">
                <label className="label" htmlFor="privateCoach">
                  Who is taking the privates?
                </label>
                <select
                  id="privateCoach"
                  className="field"
                  value={privateCoach}
                  dir="auto"
                  onChange={(e) => setPrivateCoach(e.target.value)}
                >
                  <option value="">Choose a coach…</option>
                  {coachOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                      {classCoach && name === classCoach ? ' — teaches this class' : ''}
                    </option>
                  ))}
                </select>
                {err('privateCoach') && (
                  <p className="mt-2 text-sm text-status-expired">{err('privateCoach')}</p>
                )}
                {coachOptions.length === 0 && (
                  <p className="mt-2 text-sm text-status-expiring">
                    No coaches set up yet. Add them in Settings → Coaches or
                    Private-training coaches.
                  </p>
                )}
              </div>
            )}
          </section>

          {/* Term */}
          <section className="card p-6">
            <h2 className="mb-4 text-xl text-paper">How long?</h2>
            <div className="grid grid-cols-3 gap-3">
              {TERM_MONTHS.map((m) => {
                const p = (() => {
                  const key = priceKey(ageGroup, mode, membership, m);
                  const row = schedule.find((r) => r.price_key === key && r.deleted_at === null);
                  return row?.price_piastres ?? defaultPriceFor(ageGroup, mode, membership, m);
                })();
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMonths(m)}
                    className={`min-h-touch rounded-btn border-2 px-2 py-3 transition-all duration-brand ${
                      months === m
                        ? 'border-brand bg-brand text-paper'
                        : 'border-edge bg-surface hover:border-brand'
                    }`}
                  >
                    <div className="tabular text-2xl font-bold">{m}</div>
                    <div className="text-[11px] uppercase tracking-button opacity-70">
                      {m === 1 ? 'month' : 'months'}
                    </div>
                    {classKey && (
                      <div className="tabular mt-1 text-sm font-semibold">
                        {formatEgp(p, { showCurrency: false, hideZeroFraction: true })}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            {err('months') && <p className="mt-2 text-sm text-status-expired">{err('months')}</p>}

            <button
              type="button"
              onClick={() => setShowExtras((s) => !s)}
              className="mt-4 text-sm font-semibold text-white/40 underline-offset-4 hover:text-brand hover:underline"
            >
              {showExtras ? '− Hide' : '+ Start date, discount & notes'}
            </button>

            {showExtras && (
              <div className="mt-4 grid gap-4 border-t border-edge pt-4 sm:grid-cols-2">
                <div>
                  <label className="label" htmlFor="startDate">
                    Start date
                  </label>
                  <input
                    id="startDate"
                    type="date"
                    className="field tabular"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                  {err('startDate') && (
                    <p className="mt-1 text-sm text-status-expired">{err('startDate')}</p>
                  )}
                </div>
                <div>
                  <label className="label" htmlFor="discount">
                    Discount
                  </label>
                  <MoneyInput
                    id="discount"
                    valuePiastres={discount}
                    onChange={(p) => setDiscount(p ?? 0)}
                  />
                  {err('discount') && (
                    <p className="mt-1 text-sm text-status-expired">{err('discount')}</p>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <label className="label" htmlFor="notes">
                    Notes
                  </label>
                  <input
                    id="notes"
                    dir="auto"
                    className="field"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Anything worth remembering"
                  />
                </div>
              </div>
            )}
          </section>

          {/* Payment */}
          <section className="card p-6">
            <h2 className="mb-4 text-xl text-paper">Paying now</h2>

            <div className="mb-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setPaidNow(totalDue)}
                className="rounded-btn border-2 border-edge px-4 py-2 text-sm font-bold uppercase tracking-button hover:border-brand"
              >
                Full amount
              </button>
              <button
                type="button"
                onClick={() => setPaidNow(0)}
                className="rounded-btn border-2 border-edge px-4 py-2 text-sm font-bold uppercase tracking-button hover:border-brand"
              >
                Nothing yet
              </button>
            </div>

            <MoneyInput valuePiastres={paidNow} onChange={(p) => setPaidNow(p ?? 0)} />
            {err('amountPaid') && (
              <p className="mt-1 text-sm text-status-expired">{err('amountPaid')}</p>
            )}

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <div className="label">Method</div>
                <div className="flex gap-2">
                  {PAYMENT_METHODS.map((m) => (
                    <Toggle
                      key={m}
                      active={method === m}
                      onClick={() => setMethod(m)}
                      label={PAYMENT_METHOD_LABELS[m]}
                    />
                  ))}
                </div>
              </div>
              <div>
                <label className="label" htmlFor="receivedBy">
                  Received by
                </label>
                <input
                  id="receivedBy"
                  className="field"
                  value={receivedBy}
                  onChange={(e) => setReceivedBy(e.target.value)}
                  placeholder="Your name"
                />
              </div>
            </div>
          </section>
        </div>

        {/* ---------------- live summary ---------------- */}
        <aside className="lg:sticky lg:top-28 lg:self-start">
          <div className="card overflow-hidden">
            <div className="border-b border-edge bg-black/40 p-5">
              <div className="label mb-1">Ends on</div>
              <div className="text-3xl font-bold leading-tight text-brand">
                {classKey && endDate ? formatDisplayDate(endDate) : '—'}
              </div>
              <div className="mt-1 text-sm text-white/40">
                {classKey ? classLabel(classKey) : 'Choose a class'}
                {classKey && isPrivate ? ' · Private' : ''}
              </div>
            </div>

            <dl className="space-y-3 p-5">
              <Row
                label={`${months} ${months === 1 ? 'month' : 'months'} · ${
                  clubMember ? 'Club member' : 'Non-member'
                }`}
              >
                {formatEgp(price)}
              </Row>
              {price > 0 && months > 1 && (
                <p className="-mt-1 text-xs text-white/30">
                  Works out at {formatEgp(effectiveMonthlyRate(price, months), {
                    hideZeroFraction: true,
                  })}
                  /month
                </p>
              )}
              {discount > 0 && (
                <Row label="Discount" tone="text-status-expiring">
                  −{formatEgp(discount)}
                </Row>
              )}

              <div className="border-t border-edge pt-3">
                <div className="label mb-1">Total due</div>
                <div className="tabular text-4xl font-bold">{formatEgp(totalDue)}</div>
              </div>

              <Row label="Paid now">{formatEgp(paidNow)}</Row>

              <div
                className={`rounded-btn border p-4 ${
                  remaining > 0
                    ? 'border-status-due/40 bg-status-dueBg'
                    : 'border-status-active/40 bg-status-activeBg'
                }`}
              >
                <div className="label mb-1">Remaining</div>
                <div
                  className={`tabular text-3xl font-bold ${
                    remaining > 0 ? 'text-status-due' : 'text-status-active'
                  }`}
                >
                  {remaining > 0 ? formatEgp(remaining) : 'Paid in full'}
                </div>
              </div>
            </dl>

            <div className="p-5 pt-0">
              {saveError && (
                <p className="mb-3 rounded-btn border border-status-expired/40 bg-status-expiredBg p-3 text-sm text-status-expired">
                  {saveError}
                </p>
              )}
              <button onClick={save} disabled={saving} className="btn w-full">
                {saving ? 'Saving…' : 'Save subscription'}
              </button>
              {touched && !canSave && !saving && (
                <p className="mt-2 text-center text-sm text-status-expired">
                  {Object.values(errors)[0]}
                </p>
              )}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Toggle({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`min-h-touch flex-1 rounded-btn border-2 px-3 text-sm font-bold uppercase
                  tracking-button transition-all duration-brand ${
                    active
                      ? 'border-brand bg-brand text-paper'
                      : 'border-edge bg-surface text-white/60 hover:border-brand'
                  }`}
    >
      {label}
    </button>
  );
}

function Row({
  label,
  children,
  tone = '',
}: {
  label: string;
  children: React.ReactNode;
  tone?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-sm text-white/50">{label}</dt>
      <dd className={`tabular font-semibold ${tone}`}>{children}</dd>
    </div>
  );
}
